import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Compass, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/app/api";
import { markJourneyStarted } from "@/lib/referral";
import { useAuth } from "@/app/AuthContext";
import { Footer } from "@/components/site/Footer";
import { Navbar } from "@/components/site/Navbar";
import { Seo } from "@/components/site/Seo";
import { JourneyStepper } from "@/components/journey/Stepper";
import { LanguageStep } from "@/components/journey/steps/LanguageStep";
import { BasedStep } from "@/components/journey/steps/BasedStep";
import { LocationStep } from "@/components/journey/steps/LocationStep";
import { StudyAreaStep } from "@/components/journey/steps/StudyAreaStep";
import { ProvidersStep } from "@/components/journey/steps/ProvidersStep";
import { EligibilityResultStep } from "@/components/journey/steps/EligibilityResultStep";
import { EducationStep, educationIsInsufficient } from "@/components/journey/steps/questions/EducationStep";
import { EnglishStep } from "@/components/journey/steps/questions/EnglishStep";
import { FundingQuestionStep } from "@/components/journey/steps/questions/FundingQuestionStep";
import { UkStatusStep } from "@/components/journey/steps/questions/UkStatusStep";
import { RightToWorkStep } from "@/components/journey/steps/questions/RightToWorkStep";
import { ProfileStep } from "@/components/journey/steps/questions/ProfileStep";
import { LEVEL_FROM_AI } from "@/components/journey/options";
import { loadJourneySession, saveJourneySession, saveLatestEligibilityResult } from "@/lib/journey";
import {
  assessEducationAnswers,
  fetchJourneyOptions,
  fetchLocationOptions,
  fetchOpportunity,
  identifyInstitution,
  previewNavigator,
  resumeJourney,
  runOpportunityEligibility,
  saveJourney,
} from "@/lib/journeyApi";

const opportunityNeedsEnglish = (opportunity) => {
  const requirements = opportunity?.requirements;
  if (!Array.isArray(requirements) || requirements.length === 0) return true;
  return requirements.some((requirement) => requirement.category === "english");
};

export default function Journey() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isLearner = user?.role === "individual";
  const [initialSession] = useState(() => loadJourneySession());
  const [step, setStep] = useState(() => initialSession.__step || 0);
  const [maxStep, setMaxStep] = useState(() => initialSession.__step || 0);
  const [answers, setAnswers] = useState(() => {
    const { __step, ...rest } = initialSession;
    return { language: "en-GB", ...rest };
  });
  const [options, setOptions] = useState([]);
  const [locationChoices, setLocationChoices] = useState([]);
  const [locationMessage, setLocationMessage] = useState("");
  const [locationLoading, setLocationLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [resultsDirty, setResultsDirty] = useState(false);
  const [chosenOpportunity, setChosenOpportunity] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [eligibilityError, setEligibilityError] = useState("");
  const [eligibilityDirty, setEligibilityDirty] = useState(false);
  const [assessingEducation, setAssessingEducation] = useState(false);
  const [institutionCandidate, setInstitutionCandidate] = useState(null);
  const [institutionLoading, setInstitutionLoading] = useState(false);
  const [profileChoice, setProfileChoice] = useState(null);

  useEffect(() => {
    fetchJourneyOptions().then(setOptions).catch(() => setOptions([]));
    markJourneyStarted();
  }, []);
  useEffect(() => { saveJourneySession({ ...answers, __step: step }); }, [answers, step]);
  useEffect(() => { setMaxStep((current) => Math.max(current, step)); }, [step]);

  // Restore a saved journey via secure resume link (/journey?resume=<token>)
  useEffect(() => {
    const token = searchParams.get("resume");
    if (!token) return;
    resumeJourney(token)
      .then((data) => {
        setAnswers({ ...(data.answers || {}), ...(data.language ? { language: data.language } : {}) });
        setStep(0);
        setMaxStep(0);
        toast.success("Your saved journey has been restored.");
      })
      .catch(() => toast.error("That saved journey could not be found."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore a previously chosen opportunity after a session reload
  useEffect(() => {
    if (answers.chosen_opportunity_slug && !chosenOpportunity) {
      fetchOpportunity(answers.chosen_opportunity_slug).then(setChosenOpportunity).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subjectAreas = useMemo(() => [...new Set(options.map((option) => option.subject_area).filter(Boolean))].sort(), [options]);

  const steps = useMemo(() => {
    const base = ["Language", "Where you’re based", "Location", "Study area", "Providers"];
    if (answers.chosen_opportunity_slug && chosenOpportunity) {
      base.push("Education");
      if (opportunityNeedsEnglish(chosenOpportunity)) base.push("English");
      base.push("Funding");
      if (["student_finance", "apprenticeship_employer"].includes(answers.funding_intention) || chosenOpportunity.opportunity_type === "apprenticeship") base.push("UK status");
      if (chosenOpportunity.opportunity_type === "apprenticeship") base.push("Right to work");
      if (isLearner) base.push("Your profile");
      base.push("Your eligibility");
    }
    return base;
  }, [answers.chosen_opportunity_slug, answers.funding_intention, chosenOpportunity, isLearner]);
  const stepName = steps[Math.min(step, steps.length - 1)];

  const markChanged = () => {
    if (preview) setResultsDirty(true);
    if (eligibility) setEligibilityDirty(true);
  };
  const update = (key, value) => {
    markChanged();
    setAnswers((current) => ({ ...current, [key]: value }));
  };
  const updateMany = (patch) => {
    markChanged();
    setAnswers((current) => ({ ...current, ...patch }));
  };

  const updateBased = (value) => {
    markChanged();
    setLocationChoices([]);
    setLocationMessage("");
    setAnswers((current) => ({
      ...current,
      current_location: value,
      location: "", location_preference: "", preferred_city: "",
      outside_country: "", outside_city: "",
    }));
  };

  const updateLocation = (value) => {
    markChanged();
    setLocationChoices([]);
    setLocationMessage("");
    setAnswers((current) => ({ ...current, location: value, location_preference: "", preferred_city: "" }));
  };

  const findLocationOptions = async () => {
    setLocationLoading(true);
    setLocationMessage("");
    try {
      const data = await fetchLocationOptions(answers.location);
      setLocationChoices(data.options.map((option) => [option.id, option.label, option.description]));
      setLocationMessage(data.message || "");
    } catch (requestError) {
      setLocationMessage(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setLocationLoading(false);
    }
  };

  const selectLocationPreference = (id) => {
    markChanged();
    setAnswers((current) => ({
      ...current,
      location_preference: id,
      preferred_city: id.startsWith("city:") ? id.slice(5) : "",
    }));
  };

  const updateStudyArea = (value) => {
    markChanged();
    setAnswers((current) => ({
      ...current,
      subject_area: value,
      subject_interest: value === "something_else" ? current.subject_interest : "",
    }));
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    setPreviewError("");
    try {
      setPreview(await previewNavigator(answers));
      setResultsDirty(false);
    } catch (requestError) {
      setPreviewError(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (stepName === "Providers" && !previewLoading && (!preview || resultsDirty)) loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepName]);

  const chooseOpportunity = async (slug) => {
    try {
      const opportunity = await fetchOpportunity(slug);
      setChosenOpportunity(opportunity);
      setEligibility(null);
      setEligibilityDirty(false);
      update("chosen_opportunity_slug", slug);
      setStep(5);
    } catch (requestError) {
      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
    }
  };

  const runEligibilityCheck = async () => {
    setEligibilityLoading(true);
    setEligibilityError("");
    try {
      const data = await runOpportunityEligibility(answers.chosen_opportunity_slug, {
        answers,
        useProfile: profileChoice === "yes",
        persist: false,
      });
      setEligibility(data);
      if (!isLearner) saveLatestEligibilityResult(data);
      setEligibilityDirty(false);
    } catch (requestError) {
      setEligibilityError(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setEligibilityLoading(false);
    }
  };

  useEffect(() => {
    if (stepName === "Your eligibility" && !eligibilityLoading && (!eligibility || eligibilityDirty)) runEligibilityCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepName]);

  const assessEducation = async () => {
    setAssessingEducation(true);
    try {
      const data = await assessEducationAnswers(answers);
      update("education_assessment", data);
      toast.success("Potential UK level assessed.");
    } catch (requestError) {
      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setAssessingEducation(false);
    }
  };

  const useAssessedLevel = () => {
    const mapped = LEVEL_FROM_AI[answers.education_assessment?.potential_uk_level];
    if (mapped) update("highest_education", mapped);
  };

  const searchInstitution = async () => {
    setInstitutionLoading(true);
    try {
      const data = await identifyInstitution({
        institution_name: answers.institution_search,
        country: answers.education_country || "",
        education_description: answers.education_description || "",
      });
      if (data?.name) setInstitutionCandidate(data);
      else toast.info("We couldn’t identify that institution — try the full official name or add a little more detail.");
    } catch (requestError) {
      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setInstitutionLoading(false);
    }
  };

  const confirmInstitution = () => {
    updateMany({
      education_institution: institutionCandidate.name,
      education_institution_type: institutionCandidate.institution_type || "",
      education_institution_country: institutionCandidate.country || "",
    });
    setInstitutionCandidate(null);
  };

  const selectIntake = (id) => {
    // Intake choice must not trigger an eligibility recalculation — it is application context
    const intake = eligibility?.intakes?.available?.find((item) => item.id === id);
    setAnswers((current) => ({
      ...current,
      preferred_intake_id: id,
      preferred_intake_label: id === "flexible" ? "I’m flexible" : (intake?.intake_label || ""),
    }));
  };

  const handleSaveJourney = async ({ contact_email, contact_mobile }) => {
    try {
      await saveJourney({ contact_email, contact_mobile, answers, language: answers.language });
      toast.success("Journey saved. We’ll only contact you once if you leave before finishing.");
      return true;
    } catch (requestError) {
      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
      return false;
    }
  };

  const canContinue = () => {
    if (stepName === "Language") return Boolean(answers.language);
    if (stepName === "Where you’re based") return Boolean(answers.current_location);
    if (stepName === "Location") {
      return answers.current_location === "outside_uk"
        ? Boolean(answers.outside_country)
        : Boolean(answers.location && answers.location_preference);
    }
    if (stepName === "Study area") return answers.subject_area === "something_else" ? Boolean(answers.subject_interest) : Boolean(answers.subject_area);
    if (stepName === "Education") {
      const basics = Boolean(answers.education_country && answers.highest_education && answers.award_status);
      if (!basics) return false;
      if (educationIsInsufficient(answers)) {
        return Boolean(answers.education_institution || answers.education_description || answers.qualification_name);
      }
      return true;
    }
    if (stepName === "English") {
      if (!answers.english_status) return false;
      if (answers.english_status === "needs_assessment") {
        return Boolean(answers.english_reading_answer && answers.english_vocabulary_answer && answers.english_grammar_answer && answers.english_writing_sample);
      }
      return true;
    }
    if (stepName === "Funding") return Boolean(answers.funding_intention);
    if (stepName === "UK status") return Boolean(answers.uk_status);
    if (stepName === "Right to work") return Boolean(answers.right_to_work);
    if (stepName === "Your profile") return Boolean(profileChoice);
    return true;
  };

  const goToStep = (index) => setStep(index);
  const next = () => setStep((current) => Math.min(current + 1, steps.length - 1));
  const showNavigation = stepName !== "Providers" && stepName !== "Your eligibility";

  return (
    <div data-testid="journey-page" className="min-h-screen bg-surface">
      <Seo title="Start your education journey — OpenDegree" description="Tell OpenDegree where you are and what you want to study. We work out the complicated parts and show you the education options that make sense for you." path="/journey" />
      <Navbar />
      <main className="mx-auto max-w-6xl px-5 pb-20 pt-28 sm:px-8">
        <div className="max-w-3xl">
          <p className="inline-flex items-center gap-2 rounded-full bg-cobalt/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-cobalt"><Compass className="h-4 w-4" /> Education navigator</p>
          <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-ink sm:text-5xl">Tell us where you are and what you’re looking for.</h1>
          <p className="mt-4 text-base leading-8 text-slatey sm:text-lg">OpenDegree works out the complicated parts behind the scenes and only asks when it genuinely needs something.</p>
        </div>

        <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-4 sm:p-6">
          <JourneyStepper steps={steps} step={step} maxStep={maxStep} resultsReady={Boolean(eligibility)} onGo={goToStep} />
        </div>

        <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_8px_30px_rgba(10,37,64,0.04)] sm:p-8" data-testid={`journey-step-${step}`}>
          {stepName === "Language" && (
            <LanguageStep answers={answers} update={update} onSaveJourney={handleSaveJourney} />
          )}

          {stepName === "Where you’re based" && (
            <BasedStep value={answers.current_location} onChange={updateBased} />
          )}

          {stepName === "Location" && (
            <LocationStep answers={answers} update={update} onLocationChange={updateLocation} onFindOptions={findLocationOptions}
              locationChoices={locationChoices} locationMessage={locationMessage} locationLoading={locationLoading} onSelectPreference={selectLocationPreference} />
          )}

          {stepName === "Study area" && (
            <StudyAreaStep subjectAreas={subjectAreas} answers={answers} onSelectArea={updateStudyArea} update={update} />
          )}

          {stepName === "Providers" && (
            <ProvidersStep preview={preview} loading={previewLoading} error={previewError} onChooseOpportunity={chooseOpportunity} />
          )}

          {stepName === "Education" && (
            <EducationStep answers={answers} update={update} updateMany={updateMany}
              assessingEducation={assessingEducation} onAssessEducation={assessEducation} onUseAssessedLevel={useAssessedLevel}
              institutionCandidate={institutionCandidate} institutionLoading={institutionLoading}
              onSearchInstitution={searchInstitution} onConfirmInstitution={confirmInstitution} onRejectInstitution={() => setInstitutionCandidate(null)} />
          )}

          {stepName === "English" && (
            <EnglishStep answers={answers} update={update} />
          )}

          {stepName === "Funding" && (
            <FundingQuestionStep answers={answers} opportunity={chosenOpportunity} onChange={(v) => update("funding_intention", v)} />
          )}

          {stepName === "UK status" && (
            <UkStatusStep answers={answers} update={update} />
          )}

          {stepName === "Right to work" && (
            <RightToWorkStep answers={answers} update={update} />
          )}

          {stepName === "Your profile" && (
            <ProfileStep profileChoice={profileChoice} onChange={setProfileChoice} />
          )}

          {stepName === "Your eligibility" && (
            <EligibilityResultStep result={eligibility} loading={eligibilityLoading} error={eligibilityError}
              opportunity={chosenOpportunity} onBackToProviders={() => setStep(4)} user={user}
              answers={answers} onSelectIntake={selectIntake} />
          )}

          {showNavigation && (
            <div className="mt-8 flex items-center justify-between gap-3 border-t border-slate-100 pt-5">
              <button onClick={() => setStep((current) => Math.max(current - 1, 0))} disabled={step === 0} data-testid="journey-back-button" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-3 text-sm font-bold text-ink disabled:opacity-40"><ArrowLeft className="h-4 w-4" /> Back</button>
              <button onClick={next} disabled={!canContinue() || previewLoading || assessingEducation} data-testid="journey-continue-button" className="inline-flex items-center gap-2 rounded-full bg-cobalt px-6 py-3 text-sm font-bold text-white disabled:opacity-50">
                {(previewLoading || assessingEducation) && <Loader2 className="h-4 w-4 animate-spin" />} Continue <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </section>

        <p className="mt-8 text-center text-xs leading-5 text-slatey/70" data-testid="journey-privacy-note">
          Pre-account answers are stored only in this browser session and are discarded if the session ends, unless you choose to save your journey. If you leave before completing your registration, we may contact you once by email or text with a link to continue where you stopped.
        </p>
      </main>
      <Footer />
    </div>
  );
}
